import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import {
  BudgetFormDialog,
  linesFromBudget,
  type BudgetFormSubmit,
} from '@/components/spending/budget-form-dialog';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MoneyDisplay } from '@/components/shared/money-display';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS, hasPermission, useCurrentMembership } from '@/lib/permissions';
import { formatDate } from '@/utils/format';

interface BudgetLine {
  id: string;
  fundId: string | null;
  categoryId: string | null;
  lineType: 'income' | 'expense';
  plannedAmountMinor: number;
  currency: string;
  notes: string | null;
}

interface Budget {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: 'draft' | 'active' | 'closed';
  currency: string;
  lines: BudgetLine[];
}

interface VarianceRow {
  id: string;
  lineType: 'income' | 'expense';
  targetName: string | null;
  notes: string | null;
  plannedAmountMinor: number;
  actualAmountMinor: number;
  varianceMinor: number;
  variancePercent: number | null;
  isFavourable: boolean;
  currency: string;
}

const statusVariant = (status: Budget['status']) => {
  if (status === 'active') return 'success' as const;
  if (status === 'closed') return 'neutral' as const;
  return 'warning' as const;
};

export const BudgetDetailPage = () => {
  const { budgetId = '' } = useParams();
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canEdit = hasPermission(membership, PERMISSIONS.BUDGETS_EDIT);
  const canApprove = hasPermission(membership, PERMISSIONS.BUDGETS_APPROVE);
  const [editOpen, setEditOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['budgets', budgetId],
    queryFn: () => api.get<Budget>(`/budgets/${budgetId}`),
    enabled: Boolean(budgetId),
  });

  const variance = useQuery({
    queryKey: ['budgets', budgetId, 'variance'],
    queryFn: () =>
      api.get<{ budget: Budget; data: VarianceRow[] }>(`/budgets/${budgetId}/variance`),
    enabled: Boolean(budgetId) && detail.data?.status !== 'draft',
  });

  const update = useMutation({
    mutationFn: (values: BudgetFormSubmit) =>
      api.patch<Budget>(`/budgets/${budgetId}`, {
        name: values.name,
        periodStart: values.periodStart,
        periodEnd: values.periodEnd,
        lines: values.lines,
      }),
    onSuccess: () => {
      toast.success(t('budgets.saved'));
      setEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['budgets', budgetId] });
      void queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const activate = useMutation({
    mutationFn: () => api.post<Budget>(`/budgets/${budgetId}/activate`, {}),
    onSuccess: () => {
      toast.success(t('budgets.activated'));
      setActivateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['budgets', budgetId] });
      void queryClient.invalidateQueries({ queryKey: ['budgets'] });
      void queryClient.invalidateQueries({ queryKey: ['budgets', budgetId, 'variance'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const close = useMutation({
    mutationFn: () => api.post<Budget>(`/budgets/${budgetId}/close`, {}),
    onSuccess: () => {
      toast.success(t('budgets.closed'));
      setCloseOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['budgets', budgetId] });
      void queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const budget = detail.data;
  const varianceRows = variance.data?.data ?? [];

  const draftColumns: DataTableColumn<BudgetLine>[] = [
    {
      id: 'type',
      header: t('budgets.columns.lineType'),
      cell: (row) => t(`budgets.lineType.${row.lineType}`),
    },
    {
      id: 'planned',
      header: t('budgets.columns.planned'),
      money: (row) => ({ amount: row.plannedAmountMinor, currency: row.currency }),
    },
    {
      id: 'notes',
      header: t('budgets.columns.notes'),
      cell: (row) => row.notes || '—',
    },
  ];

  const varianceColumns: DataTableColumn<VarianceRow>[] = [
    {
      id: 'target',
      header: t('budgets.columns.target'),
      cell: (row) => row.targetName || '—',
    },
    {
      id: 'type',
      header: t('budgets.columns.lineType'),
      cell: (row) => t(`budgets.lineType.${row.lineType}`),
    },
    {
      id: 'planned',
      header: t('budgets.columns.planned'),
      money: (row) => ({ amount: row.plannedAmountMinor, currency: row.currency }),
    },
    {
      id: 'actual',
      header: t('budgets.columns.actual'),
      money: (row) => ({ amount: row.actualAmountMinor, currency: row.currency }),
    },
    {
      id: 'variance',
      header: t('budgets.columns.variance'),
      cell: (row) => (
        <MoneyDisplay amount={row.varianceMinor} currency={row.currency} colorBySign />
      ),
    },
    {
      id: 'percent',
      header: t('budgets.columns.variancePercent'),
      cell: (row) =>
        row.variancePercent === null ? '—' : t('budgets.percentValue', { value: row.variancePercent }),
    },
    {
      id: 'direction',
      header: t('budgets.columns.direction'),
      cell: (row) => (
        <Badge variant={row.isFavourable ? 'success' : 'danger'}>
          {row.isFavourable ? t('budgets.favourable') : t('budgets.unfavourable')}
        </Badge>
      ),
    },
  ];

  const draftTotals = useMemo(() => {
    if (!budget || budget.lines.length === 0) return undefined;
    return {
      planned: {
        amount: budget.lines.reduce((sum, line) => sum + line.plannedAmountMinor, 0),
        currency: budget.currency,
      },
    };
  }, [budget]);

  const varianceTotals = useMemo(() => {
    if (varianceRows.length === 0) return undefined;
    const currency = varianceRows[0]?.currency ?? budget?.currency;
    if (!currency) return undefined;
    return {
      planned: {
        amount: varianceRows.reduce((sum, row) => sum + row.plannedAmountMinor, 0),
        currency,
      },
      actual: {
        amount: varianceRows.reduce((sum, row) => sum + row.actualAmountMinor, 0),
        currency,
      },
    };
  }, [budget?.currency, varianceRows]);

  const editInitial = useMemo(() => {
    if (!budget) return undefined;
    return {
      name: budget.name,
      periodStart: budget.periodStart,
      periodEnd: budget.periodEnd,
      lines: linesFromBudget(budget.lines),
    };
  }, [budget]);

  if (detail.isLoading) {
    return (
      <div>
        <PageHeader title={t('budgets.title')} />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      </div>
    );
  }

  if (detail.isError || !budget) {
    return (
      <div>
        <PageHeader
          title={t('budgets.title')}
          breadcrumbs={[{ label: t('budgets.title'), to: '/budgets' }]}
        />
        <p className="text-body-sm text-fg-secondary">{t('budgets.loadError')}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => void navigate('/budgets')}>
            {t('budgets.backToList')}
          </Button>
          <Button onClick={() => void detail.refetch()}>{tCommon('action.retry')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={budget.name}
        description={`${formatDate(budget.periodStart)} – ${formatDate(budget.periodEnd)}`}
        breadcrumbs={[
          { label: t('budgets.title'), to: '/budgets' },
          { label: budget.name },
        ]}
        meta={<Badge variant={statusVariant(budget.status)}>{t(`budgets.status.${budget.status}`)}</Badge>}
        headerActions={
          <div className="flex flex-wrap items-center gap-2">
            {budget.status === 'draft' && canEdit ? (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                {t('budgets.edit')}
              </Button>
            ) : null}
            {budget.status === 'draft' && canApprove ? (
              <Button onClick={() => setActivateOpen(true)}>{t('budgets.activate')}</Button>
            ) : null}
            {budget.status === 'active' && canApprove ? (
              <Button variant="outline" onClick={() => setCloseOpen(true)}>
                {t('budgets.close')}
              </Button>
            ) : null}
          </div>
        }
      />

      {budget.status === 'draft' ? (
        <DataTable
          rows={budget.lines}
          columns={draftColumns}
          rowKey={(row) => row.id}
          empty={<p className="text-body-sm text-fg-secondary">{t('budgets.linesEmpty')}</p>}
          totals={draftTotals}
          totalsLabel={t('budgets.totals')}
        />
      ) : (
        <DataTable
          rows={varianceRows}
          columns={varianceColumns}
          rowKey={(row) => row.id}
          loading={variance.isLoading}
          empty={<p className="text-body-sm text-fg-secondary">{t('budgets.varianceEmpty')}</p>}
          totals={varianceTotals}
          totalsLabel={t('budgets.totals')}
        />
      )}

      {editInitial ? (
        <BudgetFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          mode="edit"
          initial={editInitial}
          submitting={update.isPending}
          onSubmit={(values) => update.mutate(values)}
        />
      ) : null}

      <ConfirmDialog
        open={activateOpen}
        onOpenChange={setActivateOpen}
        title={t('confirm.activateBudget.title', { name: budget.name })}
        description={t('confirm.activateBudget.description')}
        confirmLabel={t('confirm.activateBudget.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={activate.isPending}
        onConfirm={() => activate.mutate()}
      />

      <ConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={t('confirm.closeBudget.title', { name: budget.name })}
        description={t('confirm.closeBudget.description')}
        confirmLabel={t('confirm.closeBudget.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={close.isPending}
        onConfirm={() => close.mutate()}
      />
    </div>
  );
};
